// MCHD Room Scheduler - Clean Minimal Login Page
// Hospital-grade security with clean, professional design

import { useState } from 'react';
import {
  Title,
  Text,
  Group,
  Stack,
  Alert,
  Box,
  Button,
  TextInput,
  PasswordInput,
  Card,
} from '@mantine/core';
import { 
  IconAlertCircle, 
  IconMail,
  IconLock,
  IconArrowRight,
  IconShield,
} from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { LoginRequest } from '../api';

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginPageProps {
  onLogin: (data: LoginRequest) => void;
  isLoading: boolean;
  error: string | null;
}

function LoginPage({ onLogin, isLoading, error }: LoginPageProps): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<LoginFormData>({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(value)) return 'Please enter a valid email address';
        if (!value.endsWith('@moh.gov.sa')) return 'Only @moh.gov.sa emails allowed.';
        return null;
      },
      password: (value) => {
        if (!value) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        return null;
      },
    },
  });

  const handleSubmit = async (values: LoginFormData) => {
    setIsSubmitting(true);
    
    try {
      // Convert to LoginRequest format (only email and password)
      const loginData: LoginRequest = {
        email: values.email,
        password: values.password,
      };
      
      await onLogin(loginData);
      notifications.show({
        title: 'Welcome back!',
        message: 'You have been successfully logged in.',
        color: 'mchdBlue',
        icon: <IconShield size={16} />,
      });
    } catch (err) {
      notifications.show({
        title: 'Login failed',
        message: 'Please check your credentials and try again.',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, label: '', color: '' };
    if (password.length < 6) return { strength: 25, label: 'Weak', color: 'red' };
    if (password.length < 8) return { strength: 50, label: 'Fair', color: 'orange' };
    if (password.length < 12) return { strength: 75, label: 'Good', color: 'blue' };
    return { strength: 100, label: 'Strong', color: 'green' };
  };

  const passwordStrength = getPasswordStrength(form.values.password);

  return (
    <Box style={{ 
      minHeight: '100vh',
      backgroundColor: '#F8F8F8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <Card 
        shadow="sm"
        padding="2rem"
        radius="md"
        style={{
          width: '100%',
          maxWidth: '480px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.02), 0 2px 4px rgba(0, 0, 0, 0.01)'
        }}
      >
        {/* Logo */}
        <Box mb="xl" ta="center">
          <img 
            src="/logo/logo.svg" 
            alt="MCHD Logo" 
            style={{
              width: '360px',
              height: 'auto',
              marginBottom: '1rem'
            }}
          />
        </Box>

        {/* Welcome Section */}
        <Box mb="xl" ta="center">
          <Title 
            order={1} 
            ta="center" 
            mb="md" 
            c="dark"
            fw={700}
            size="2rem"
            style={{ 
              fontFamily: 'Rubik, Inter, system-ui, sans-serif',
            }}
          >
            Welcome Back
          </Title>
          <Text 
            size="md" 
            c="dimmed" 
            ta="center" 
            lh={1.6}
            fw={400}
            style={{ 
              fontFamily: 'Rubik, Inter, system-ui, sans-serif',
            }}
          >
            Sign in to access the room scheduling system and manage your healthcare operations efficiently.
          </Text>
        </Box>

        {/* Login Form */}
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {/* Error Alert */}
            {error && (
              <Alert 
                icon={<IconAlertCircle size={16} />} 
                title="Authentication Error" 
                color="red" 
                radius="md"
                variant="light"
              >
                {error}
              </Alert>
            )}

            {/* Email Input */}
            <TextInput
              label="Email"
              placeholder="Enter your @moh.gov.sa email"
              leftSection={<IconMail size={18} />}
              required
              size="md"
              radius="md"
              styles={{
                input: {
                  border: '1px solid #D1D5DB',
                  '&:focus': {
                    borderColor: '#28A8E0',
                    boxShadow: '0 0 0 2px rgba(40, 168, 224, 0.1)',
                  }
                }
              }}
              {...form.getInputProps('email')}
            />

            {/* Password Input */}
            <Box>
              <PasswordInput
                label="Password"
                placeholder="Enter your password"
                leftSection={<IconLock size={18} />}
                required
                size="md"
                radius="md"
                styles={{
                  input: {
                    border: '1px solid #D1D5DB',
                    '&:focus': {
                      borderColor: '#28A8E0',
                      boxShadow: '0 0 0 2px rgba(40, 168, 224, 0.1)',
                    }
                  }
                }}
                {...form.getInputProps('password')}
              />
              
              {/* Simplified Password Strength Indicator */}
              {form.values.password && (
                <Box mt="sm">
                  <Group justify="space-between" mb="xs">
                    <Text size="xs" c="dimmed" fw={500}>
                      Strength
                    </Text>
                    <Text size="xs" c={passwordStrength.color} fw={600}>
                      {passwordStrength.label}
                    </Text>
                  </Group>
                  <Box
                    style={{
                      height: 4,
                      backgroundColor: '#E5E7EB',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      style={{
                        height: '100%',
                        width: `${passwordStrength.strength}%`,
                        backgroundColor: passwordStrength.color,
                        transition: 'all 0.3s ease',
                        borderRadius: 2,
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>


            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              size="md"
              rightSection={<IconArrowRight size={20} />}
              loading={isLoading || isSubmitting}
              style={{
                backgroundColor: '#28A8E0',
                height: '48px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 600,
                fontSize: '16px',
                '&:hover': {
                  backgroundColor: '#1e8bc3',
                }
              }}
            >
              {isLoading || isSubmitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </Stack>
        </form>

        {/* Footer */}
        <Box mt="xl" ta="center">
          <Text 
            size="sm" 
            c="dimmed" 
            fw={500}
            style={{ 
              fontFamily: 'Rubik, Inter, system-ui, sans-serif',
            }}
          >
            Don't have an account?{' '}
            <Text
              component="span"
              c="#28A8E0"
              style={{ 
                cursor: 'pointer',
                textDecoration: 'none',
                fontWeight: 600,
              }}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                notifications.show({
                  title: 'Contact Administrator',
                  message: 'Account creation is managed by your system administrator. Please contact them for access.',
                  color: 'blue',
                  icon: <IconShield size={16} />,
                });
              }}
            >
              Contact Administrator
            </Text>
          </Text>
        </Box>
      </Card>
    </Box>
  );
}

export default LoginPage;